// Layer 4 (UI): polish.P9 (#657) — the Mass balance tab body (locked comp
// `metal-accounting.html` A, B's `ledgerB` with variance): one ruled sheet of
// streams, grades, recovery against design and target, loss to tails and the
// spec check. Renders `massBalanceLedger` verbatim.

import { Pill, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { vizToneColor, type VizTone } from '@/versions/v1/modules/_viz';
import { CARD_TABLE } from '@/versions/v1/modules/downtime/DowntimeCard';
import { STATE_PILL } from './BalanceStrip';
import type { LedgerRow, LedgerRun } from './MassBalanceLedger.transform';

const PILL_TONE: Record<VizTone, 'danger' | 'warning' | 'success' | 'default'> = {
  bad: 'danger',
  warn: 'warning',
  ok: 'success',
  neutral: 'default',
  info: 'default',
};

function toneStyle(tone?: VizTone) {
  return tone && tone !== 'neutral' ? { color: vizToneColor(tone) } : undefined;
}

function Meta({ runs }: { runs: readonly LedgerRun[] }) {
  if (runs.length === 0) return null;
  return (
    // The stream cell holds its name on one line; the provenance line under it
    // may wrap, so the sheet fits the card at 1280 instead of scrolling.
    <span className="block whitespace-normal text-xs font-normal text-muted">
      {runs.map((run, index) => (
        <span key={index} className={run.tone ? 'font-semibold' : undefined} style={toneStyle(run.tone)}>
          {run.text}
        </span>
      ))}
    </span>
  );
}

function RecoveryCell({ row }: { row: LedgerRow }) {
  if (row.recovery.length === 0) return <span className="text-muted">—</span>;
  return (
    <span className="block">
      {row.recovery.map((entry, index) =>
        index === 0 ? (
          <span key={entry.element} className="block" title={entry.uncertainty ?? undefined}>
            <span className="font-semibold text-ink-primary">{entry.actual}</span> {entry.element}
            <span className="block text-xs text-muted">vs {entry.design} design</span>
            <span className="block text-xs text-muted">
              {entry.target} target ·{' '}
              <span className="font-semibold" style={toneStyle(entry.varianceTone)}>
                {entry.variance}
              </span>
            </span>
          </span>
        ) : (
          <span key={entry.element} className="block text-xs text-muted" title={entry.uncertainty ?? undefined}>
            {entry.element} {entry.actual} vs {entry.design} · {entry.target} target ·{' '}
            <span className="font-semibold" style={toneStyle(entry.varianceTone)}>
              {entry.variance}
            </span>
          </span>
        ),
      )}
    </span>
  );
}

export function MassBalanceLedger({ rows }: { rows: readonly LedgerRow[] }) {
  return (
    <Commentable name="BalanceStreams">
      <div className={CARD_TABLE} data-mass-balance-ledger>
        <Table wrap={false} className="tabular-nums">
          <THead>
            <TR>
              <TH>Stream</TH>
              <TH className="text-right">Tonnes</TH>
              <TH className="text-right">Pb %</TH>
              <TH className="text-right">Zn %</TH>
              <TH className="text-right">Ag oz/t</TH>
              <TH>Recovery</TH>
              <TH>To tails</TH>
              <TH>Check</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR
                key={row.id}
                data-ledger-row={row.id}
                className={row.total ? 'border-t border-[var(--hairline-strong)]' : undefined}
              >
                <TD
                  className={cn('whitespace-nowrap font-medium !text-ink-primary', row.indent ? 'pl-6' : '', row.total ? 'font-semibold' : '')}
                  title={row.title}
                >
                  {row.stream}
                  <Meta runs={row.meta} />
                </TD>
                <TD className={cn('whitespace-nowrap text-right', row.total ? 'font-semibold !text-ink-primary' : '')}>{row.tonnes}</TD>
                {row.grades.map((grade) => (
                  <TD
                    key={grade.id}
                    className={cn('whitespace-nowrap text-right', row.total ? 'font-semibold !text-ink-primary' : '', grade.tone ? 'font-semibold' : '')}
                    style={toneStyle(grade.tone)}
                  >
                    {grade.text}
                  </TD>
                ))}
                <TD className="whitespace-nowrap">
                  <RecoveryCell row={row} />
                </TD>
                <TD className="whitespace-nowrap">
                  {row.toTails ? (
                    <span className="block">
                      <span className="font-semibold text-ink-primary">{row.toTails.lead}</span>
                      <span className="block text-xs text-muted">{row.toTails.rest}</span>
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </TD>
                <TD className="whitespace-nowrap">
                  {row.check ? (
                    <Pill tone={PILL_TONE[row.check.tone]} className={STATE_PILL}>
                      {row.check.label}
                    </Pill>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </Commentable>
  );
}
